/**
 * 「模型上传」子页。三步骤:
 *   ① 更新的模型 items:
 *       - 每 item = (name, version, category, type) + 多个 (tag, file) 二元组
 *       - name 支持"下拉选已有 + 手动输入新名字",选已有时自动预填 version/category/type
 *       - 同一 item 下点「添加其他格式」追加 tag 行
 *   ② 选择基版 (可选)
 *   ③ 目标主版本号 (可选, 留空后端按拼装规则生成)
 *
 * 点「上传并发布」:
 *   for each (item, tag_file):  POST /api/model/upload (multipart)
 *   POST /api/model/release                              → 生成新主版本
 *
 * mockup 对齐: docs/mockups/model-management-ui.html 第三 tab (模型上传)。
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Divider,
  Input,
  message,
  Select,
  Skeleton,
  Space,
  Switch,
  Tag,
} from 'antd';
import { CloseOutlined, CopyOutlined, InboxOutlined, PlusOutlined } from '@ant-design/icons';
import { useCurrentUser } from '@/common/auth/adminAcl';
import { getReleaseNameError, parseReleaseName } from '@/utils/modelReleaseName';
import { uploadVersionErrors } from '@/utils/releaseChanges';
import { ndpTypeLabel } from '@/utils/modelCatalog';
import {
  attachToRelease,
  buildRelease,
  getModelCategories,
  getModelDetail,
  getModelUploadNames,
  listModels,
  listReleaseTree,
  uploadModel,
  type AttachToReleaseItem,
  type MajorGroupTree,
  type ModelCategories,
  type ModelListItem,
  type ModelRow,
} from '@/apis/model';

/** 每 item 下的一个 (tag, file) 二元组。 */
interface TagFile {
  id: string;                   // 前端本地临时 id
  tag: string;
  file?: File;
}

interface UploadItem {
  id: string;
  name: string;
  version: string;
  category?: string;
  type?: string;
  tagFiles: TagFile[];
}

const CATEGORY_OPTIONS = [
  { label: 'perception', value: 'perception' },
  { label: 'planning', value: 'planning' },
  { label: 'prediction', value: 'prediction' },
  { label: 'control', value: 'control' },
];

const TAG_PRESETS = ['onnx', 'engine', 'x86.onnx', 'pt', 'tflite'];

const newTagFile = (tag = 'onnx'): TagFile => ({
  id: `tf${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
  tag,
});

const newItem = (): UploadItem => ({
  id: `it${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
  name: '',
  version: '',
  tagFiles: [newTagFile()],
});

const Upload: React.FC = () => {
  const [items, setItems] = useState<UploadItem[]>([newItem()]);

  // 已有模型清单 (供 ① 里 name 下拉 + 预填)
  const [existing, setExisting] = useState<ModelListItem[]>([]);
  useEffect(() => {
    listModels().then((r) => setExisting(r.items)).catch(() => setExisting([]));
  }, []);
  // 二级分类中文名（模型分类配置的 type_labels），只用于在二级分类下方提示中台类型名
  const [cats, setCats] = useState<ModelCategories | null>(null);
  useEffect(() => {
    getModelCategories().then(setCats).catch((e) => console.error('加载模型分类失败', e));
  }, []);
  // 允许上传的模型类别（系统配置「允许上传的模型类别」，管理员维护）：下拉只列这些
  const [uploadNames, setUploadNames] = useState<string[]>([]);
  useEffect(() => {
    getModelUploadNames()
      .then(setUploadNames)
      .catch((e) => message.error(`加载允许上传的模型类别失败：${e?.response?.data?.message ?? e?.message ?? e}`));
  }, []);
  const existingByName = useMemo(() => {
    const m = new Map<string, ModelListItem>();
    existing.forEach((e) => m.set(e.name, e));
    return m;
  }, [existing]);

  // ② 关联版本号
  //   - basedOn: 从下拉里选,选项按 items 里出现的 category 过滤
  //   - releaseName: 「生成新的版本号」抽屉,默认收起。用户想手动指定完整 name 时才展开;
  //     否则整块留空,交给后端自动拼 `{today}.release.{seg+1}` (kind 默认 release)。
  //     留空才自动编号;非空但不合法时明确报错,不忽略用户输入。
  const [basedOn, setBasedOn] = useState<string | undefined>(undefined);
  const [customizeName, setCustomizeName] = useState(false);
  const [releaseName, setReleaseName] = useState('');
  const [description, setDescription] = useState('');

  // 上传身份 = 当前登录用户,不再让用户手输 (行为对齐提交任务的 creator)
  const { me } = useCurrentUser();
  const uploader = me?.user?.email || '';

  // 已有主版本清单 (供基版下拉);按 items 里出现过的 category 过滤
  const [tree, setTree] = useState<MajorGroupTree[]>([]);
  const [treeLoading, setTreeLoading] = useState(true);

  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{
    ok: boolean;
    releaseName?: string;
    error?: string;
    uploadedCount?: number;
  } | null>(null);

  useEffect(() => {
    setTreeLoading(true);
    listReleaseTree()
      .then((r) => setTree(r.groups))
      .catch(() => setTree([]))
      .finally(() => setTreeLoading(false));
  }, []);

  // items 里出现的一级分类集合 (用于基版下拉过滤 + 传给后端)
  const itemCategories = useMemo(() => {
    const s = new Set<string>();
    for (const it of items) {
      if (it.category) s.add(it.category);
    }
    return Array.from(s);
  }, [items]);

  // 基版下拉:优先展示与 items category 匹配的; items 没填 category 时展示全部
  const baseOptions = useMemo(() => {
    const all = tree.flatMap((g) => g.releases);
    if (itemCategories.length === 0) return all;
    return all.filter(
      (r) => !r.category || itemCategories.includes(r.category) || r.category === 'mixed',
    );
  }, [tree, itemCategories]);

  const updateItem = (id: string, patch: Partial<UploadItem>) => {
    setItems((prev) => prev.map((it) => (it.id === id ? { ...it, ...patch } : it)));
  };
  const updateTagFile = (itemId: string, tfId: string, patch: Partial<TagFile>) => {
    setItems((prev) =>
      prev.map((it) =>
        it.id === itemId
          ? { ...it, tagFiles: it.tagFiles.map((tf) => (tf.id === tfId ? { ...tf, ...patch } : tf)) }
          : it,
      ),
    );
  };
  const addTagFile = (itemId: string) => {
    setItems((prev) =>
      prev.map((it) => (it.id === itemId ? { ...it, tagFiles: [...it.tagFiles, newTagFile()] } : it)),
    );
  };
  const removeTagFile = (itemId: string, tfId: string) => {
    setItems((prev) =>
      prev.map((it) =>
        it.id === itemId ? { ...it, tagFiles: it.tagFiles.filter((tf) => tf.id !== tfId) } : it,
      ),
    );
  };
  const removeItem = (id: string) => setItems((prev) => prev.filter((it) => it.id !== id));

  /** 选中模型类别时,预填 category/type (若这些字段用户还没手改)。版本号不预填：同一模型类别下版本号不能重复。 */
  const pickExistingName = (itemId: string, name: string) => {
    const info = existingByName.get(name);
    setItems((prev) =>
      prev.map((it) => {
        if (it.id !== itemId) return it;
        const next: UploadItem = { ...it, name };
        if (info) {
          if (!it.category) next.category = info.category || undefined;
          if (!it.type) next.type = info.type || undefined;
        }
        return next;
      }),
    );
  };

  // 校验:每个 item 必须有 name/version,至少一个 (tag, file) 完整
  const validPairs: Array<{ item: UploadItem; tf: TagFile }> = [];
  for (const it of items) {
    if (!it.name.trim() || !it.version.trim()) continue;
    for (const tf of it.tagFiles) {
      if (tf.tag.trim() && tf.file) {
        validPairs.push({ item: it, tf });
      }
    }
  }
  // 版本号检查：同一模型类别下版本号不能重复（含已停用的版本；本次提交里重复也不行）
  const [existingVersions, setExistingVersions] = useState<Record<string, string[]>>({});
  const [versionsKey, setVersionsKey] = useState(0);
  // 取已有版本失败的类别：不再阻塞提交，由后端兜底校验版本号（否则提交按钮会一直不可点）
  const [versionsFailed, setVersionsFailed] = useState<Set<string>>(new Set());
  const pickedNames = useMemo(
    () => Array.from(new Set(items.map((it) => it.name.trim()).filter(Boolean))).sort(),
    [items],
  );
  useEffect(() => {
    let cancelled = false;
    const missing = pickedNames.filter((n) => existingVersions[n] === undefined);
    if (missing.length === 0) {
      return undefined;
    }
    Promise.all(missing.map((n) => getModelDetail(n)
      .then((d) => [n, (d?.history ?? []).map((h) => h.version)] as const)
      .catch((e) => {
        console.error(`加载模型类别 ${n} 的已有版本失败`, e);
        message.error(`加载模型类别 ${n} 的已有版本失败，提交时由服务端检查版本号是否重复：${e?.response?.data?.message ?? e?.message ?? e}`);
        return [n, null] as const;
      })))
      .then((pairs) => {
        if (cancelled) {
          return;
        }
        setVersionsFailed((prev) => {
          const failed = pairs.filter(([, vs]) => !vs).map(([n]) => n);
          return failed.length ? new Set([...prev, ...failed]) : prev;
        });
        setExistingVersions((prev) => {
          const next = { ...prev };
          for (const [n, vs] of pairs) {
            if (vs) {
              next[n] = vs;
            }
          }
          return next;
        });
      });
    return () => {
      cancelled = true;
    };
    // existingVersions 只用于判断是否缺失，变化不需要重新触发
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickedNames, versionsKey]);
  const versionErrors = useMemo(() => uploadVersionErrors(items, existingVersions), [items, existingVersions]);
  const versionsPending = pickedNames.some((n) => existingVersions[n] === undefined && !versionsFailed.has(n));

  // 用户仅在展开抽屉时才可能填 releaseName; 只有空输入走后端自动拼路径。
  // 后端约束: major_group 必填 (Form(...)), release_name / release_seg / kind 都可选;
  // 走 "自动" 分支时给 major_group 传占位桶 "release", 让后端自动 seg+1 产出新 name。
  const parsedRelease = customizeName ? parseReleaseName(releaseName.trim()) : null;
  const releaseNameError = customizeName ? getReleaseNameError(releaseName) : null;

  // 提交合法性:
  //   - 有待上传文件 + 有 uploader → 基本要求
  //   - 开关 OFF (customizeName=false): 追加到基版, 必须选中 basedOn
  //   - 开关 ON: 生成新版本号, basedOn 可选
  const attachMode = !customizeName;
  const canSubmit =
    validPairs.length > 0 &&
    !!uploader.trim() &&
    !releaseNameError &&
    !busy &&
    Object.keys(versionErrors).length === 0 &&
    !versionsPending &&
    (!attachMode || !!basedOn);

  const handleSubmit = async () => {
    if (releaseNameError) {
      message.error(releaseNameError);
      return;
    }
    setBusy(true);
    setResult(null);
    let uploaded = 0;
    // 上传时收集每一行的 ModelRow, 供后续 attach 用 (按 name 聚合成 manifest.item)
    const uploadedRows: ModelRow[] = [];
    // 每个 item（一个模型类别的一个版本）一个批次号：同一版本的多个文件带同一个值，后端据此判断版本号是否重复
    const batches = new Map(items.map((it) => [it.id, `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`]));
    try {
      // ① 逐个上传 (item × tag_file)
      for (const { item, tf } of validPairs) {
        const row = await uploadModel({
          upload_batch: batches.get(item.id),
          name: item.name.trim(),
          version: item.version.trim(),
          tag: tf.tag.trim(),
          file: tf.file!,
          uploader: uploader.trim(),
          category: item.category,
          type: item.type?.trim() || undefined,
        });
        uploadedRows.push(row);
        uploaded += 1;
      }

      // ② 分两条路: 追加基版 vs 生成新版本号
      if (attachMode) {
        // 追加到已选基版:把 uploadedRows 按 name 聚合成 manifest.item
        if (!basedOn) {
          throw new Error('请先在上方选择基版');
        }
        const byName = new Map<string, AttachToReleaseItem>();
        for (const r of uploadedRows) {
          const key = r.name;
          let it = byName.get(key);
          if (!it) {
            it = {
              name: r.name,
              version: r.version,
              category: r.category,
              type: r.type,
              artifacts: {},
            };
            byName.set(key, it);
          }
          // 同名多 tag 都塞进同一个 item.artifacts;version 若前后不一致,以最后一次为准
          it.version = r.version;
          it.artifacts[r.tag] = {
            model_id: r.id,
            storage_uri: r.storage_uri,
            file_sha256: r.file_sha256,
            file_size: r.file_size,
          };
        }
        const rel = await attachToRelease(basedOn, Array.from(byName.values()), uploader.trim());
        setResult({ ok: true, releaseName: rel.name, uploadedCount: uploaded });
        message.success(`已追加 ${byName.size} 个模型到 ${rel.name}`);
      } else {
        // 生成新主版本。customizeName + parsedRelease → 用解析结果;否则占位 major_group + 后端自动 seg+1
        const buildParams = parsedRelease
          ? {
              major_group: parsedRelease.major_group,
              created_by: uploader.trim(),
              kind: parsedRelease.kind,
              release_name: releaseName.trim(),
              release_seg: parsedRelease.release_seg || undefined,
              category: itemCategories[0],
              based_on: basedOn,
              description: description || undefined,
            }
          : {
              major_group: 'release',
              created_by: uploader.trim(),
              kind: 'release' as const,
              category: itemCategories[0],
              based_on: basedOn,
              description: description || undefined,
            };
        const rel = await buildRelease(buildParams);
        setResult({ ok: true, releaseName: rel.name, uploadedCount: uploaded });
        message.success(`已生成新主版本 ${rel.name}`);
      }

      setItems([newItem()]);
      // 重新拉一次 existing, 让下一次上传能自动看到新加入的模型
      listModels().then((r) => setExisting(r.items)).catch(() => {});
      setExistingVersions({});
      setVersionsKey((k) => k + 1);
    } catch (e) {
      const msg = (e as { message?: string })?.message || String(e);
      setResult({ ok: false, error: msg, uploadedCount: uploaded });
      message.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 340px', gap: 16, alignItems: 'flex-start' }}>
      {/* 主体 */}
      <div>
        {/* Step 1: items */}
        <Section index="①" title="更新的模型 (items)" hint="每个 item 一个 (模型类别, version), 下面可挂多个 tag 文件">
          {items.map((it, idx) => (
            <ItemCard
              key={it.id}
              idx={idx}
              item={it}
              existingNames={uploadNames}
              cats={cats}
              versionError={versionErrors[it.id]}
              onNameSelect={(name) => pickExistingName(it.id, name)}
              onChange={(patch) => updateItem(it.id, patch)}
              onRemove={items.length > 1 ? () => removeItem(it.id) : undefined}
              onTagFileChange={(tfId, patch) => updateTagFile(it.id, tfId, patch)}
              onAddTagFile={() => addTagFile(it.id)}
              onRemoveTagFile={(tfId) => removeTagFile(it.id, tfId)}
            />
          ))}
          <div style={{ marginTop: 16 }}>
            <label style={labelStyle}>描述 (可选)</label>
            <Input.TextArea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="一句话说明这次发布"
              rows={2}
            />
          </div>
        </Section>

        {/* Step 2: 关联版本号 (合并了「基版」和「目标主版本号」) */}
        <Section
          index="②"
          title="关联版本号"
          hint={
            itemCategories.length > 0
              ? `基版下拉已按 items 分类过滤: ${itemCategories.join(' / ')}`
              : '给 items 填一级分类后, 基版下拉会自动按分类过滤'
          }
        >
          <Space direction="vertical" style={{ width: '100%' }} size={12}>
            <div>
              <label style={labelStyle}>
                基版 <Tag style={{ marginLeft: 4 }}>可选</Tag>{' '}
                <span style={{ color: '#8c8c8c', fontWeight: 400 }}>
                  选中后未变更的模型自动沿用
                </span>
              </label>
              {treeLoading ? (
                <Skeleton active paragraph={{ rows: 1 }} />
              ) : (
                <div style={{ display: 'flex', gap: 6 }}>
                  <Select
                    style={{ flex: 1 }}
                    value={basedOn}
                    onChange={(v) => setBasedOn(v || undefined)}
                    placeholder="-- 不选基版 (仅本次上传的模型) --"
                    allowClear
                    showSearch
                    options={baseOptions.map((r) => ({
                      label: `${r.name}${r.category ? ` · ${r.category}` : ''}${r.kind === 'dev' ? ' · dev' : ''}`,
                      value: r.name,
                    }))}
                    notFoundContent={
                      itemCategories.length > 0
                        ? '当前分类下没有历史主版本, 可以先不选基版'
                        : '暂无主版本'
                    }
                  />
                  <Button
                    icon={<CopyOutlined />}
                    disabled={!basedOn}
                    onClick={() => {
                      if (!basedOn) return;
                      navigator.clipboard
                        .writeText(basedOn)
                        .then(() => message.success(`已复制 ${basedOn}`))
                        .catch(() => message.error('复制失败'));
                    }}
                    title="复制基版 name"
                  >
                    复制
                  </Button>
                </div>
              )}
            </div>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <Switch
                  size="small"
                  checked={customizeName}
                  onChange={(v) => setCustomizeName(v)}
                />
                <label style={{ ...labelStyle, marginBottom: 0 }}>
                  生成新的版本号
                </label>
                <span style={{ color: '#8c8c8c', fontSize: 11.5 }}>
                  {customizeName
                    ? '开: 上传后拼装出新的主版本 (可手动指定 name 或让后端自动拼)'
                    : '关: 不生成新版本, 把上传的模型直接绑到上方选中的基版'}
                </span>
              </div>
              {customizeName && (
                <div style={{ marginTop: 8 }}>
                  <Input
                    value={releaseName}
                    status={releaseNameError ? 'error' : undefined}
                    onChange={(e) => setReleaseName(e.target.value)}
                    placeholder="留空自动生成；或手写 20261015.release.2 / 20261015.release.10.12.13"
                  />
                  {releaseName && parsedRelease && (
                    <div style={{ fontSize: 11.5, color: '#16a34a', marginTop: 4 }}>
                      ✓ 解析: kind=<b>{parsedRelease.kind}</b> · major_group=
                      <b>{parsedRelease.major_group}</b>
                      {parsedRelease.release_seg && (
                        <> · seg=<b>{parsedRelease.release_seg}</b></>
                      )}
                    </div>
                  )}
                  {releaseNameError && (
                    <div style={{ fontSize: 11.5, color: '#dc2626', marginTop: 4 }}>
                      {releaseNameError}
                    </div>
                  )}
                </div>
              )}
              {!customizeName && !basedOn && (
                <div style={{ fontSize: 11.5, color: '#dc2626', marginTop: 6 }}>
                  开关关闭时必须先选基版 (上方「基版」下拉)。
                </div>
              )}
            </div>
          </Space>
        </Section>

        {/* 底部 */}
        <div
          style={{
            padding: '16px 20px',
            background: '#fafafa',
            border: '1px solid #e5e7eb',
            borderRadius: 12,
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            marginTop: 16,
          }}
        >
          <div style={{ flex: 1, fontSize: 12.5, color: '#4b5563' }}>
            将上传 <b>{validPairs.length}</b> 个 tag 文件
            {attachMode ? (
              basedOn ? (
                <> · 追加到基版 <code>{basedOn}</code></>
              ) : (
                <span style={{ color: '#dc2626' }}> · 请先选择基版</span>
              )
            ) : parsedRelease ? (
              <> · 生成新版本 <code>{releaseName.trim()}</code></>
            ) : (
              <>
                {basedOn ? (
                  <> · 基于 <code>{basedOn}</code></>
                ) : (
                  <span style={{ color: '#8c8c8c' }}> · 不选基版</span>
                )}
                <span style={{ color: '#8c8c8c' }}> · 版本号自动生成</span>
              </>
            )}
          </div>
          <Button disabled={busy}>取消</Button>
          <Button type="primary" onClick={handleSubmit} disabled={!canSubmit} loading={busy}>
            {attachMode ? '上传并追加' : '上传并发布'}
          </Button>
        </div>

        {result && (
          <Alert
            type={result.ok ? 'success' : 'error'}
            showIcon
            style={{ marginTop: 16 }}
            message={result.ok ? `已生成新主版本 ${result.releaseName}` : `发布失败`}
            description={
              result.ok
                ? `${result.uploadedCount} 个 tag 文件上传成功`
                : `${result.error}${result.uploadedCount ? ` (已上传 ${result.uploadedCount} 个文件,但主版本构造失败)` : ''}`
            }
          />
        )}
      </div>

      {/* 右侧上传身份 + 帮助 */}
      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 16 }}>
        <div
          style={{
            fontSize: 11,
            color: '#8c8c8c',
            textTransform: 'uppercase',
            letterSpacing: '0.4px',
            fontWeight: 600,
            marginBottom: 6,
          }}
        >
          上传身份
        </div>
        <div
          style={{
            padding: '6px 10px',
            border: '1px solid #f0f0f0',
            borderRadius: 6,
            background: '#fafafa',
            fontSize: 13,
            fontFamily: 'SFMono-Regular,Menlo,Consolas,monospace',
            color: uploader ? '#1d1d1f' : '#8c8c8c',
          }}
        >
          {uploader || '(未登录)'}
        </div>
        <Divider style={{ margin: '16px 0' }} />
        <div style={{ fontSize: 12, color: '#6b6f76', lineHeight: 1.7 }}>
          <b>要点:</b>
          <ul style={{ paddingLeft: 18, margin: 6 }}>
            <li>模型类别只能从下拉选择 (管理员在系统配置「允许上传的模型类别」维护)</li>
            <li>同一模型类别下版本号不能重复;选类别时分类 / type 自动预填 (可覆盖)</li>
            <li>同一 (name, version) 下可挂多个 tag (onnx / engine / x86.onnx 等)</li>
            <li>sha256 相同的文件自动秒传, 不重复推 BOS</li>
            <li>文件永久保留;停用只是从新 manifest 摘掉</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

// ============================================================
// helpers
// ============================================================

const Section: React.FC<{
  index: string;
  title: React.ReactNode;
  hint?: string;
  children: React.ReactNode;
}> = ({ index, title, hint, children }) => (
  <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, marginBottom: 16 }}>
    <div
      style={{
        padding: '14px 20px',
        borderBottom: '1px solid #f0f0f0',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}
    >
      <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>
        {index} {title}
      </h3>
      {hint && <span style={{ color: '#8c8c8c', fontSize: 12 }}>{hint}</span>}
    </div>
    <div style={{ padding: '16px 20px' }}>{children}</div>
  </div>
);

const ItemCard: React.FC<{
  idx: number;
  item: UploadItem;
  existingNames: string[];
  /** 模型分类配置：二级分类下方提示中台类型中文名 */
  cats: ModelCategories | null;
  versionError?: string;
  onNameSelect: (name: string) => void;
  onChange: (patch: Partial<UploadItem>) => void;
  onRemove?: () => void;
  onTagFileChange: (tfId: string, patch: Partial<TagFile>) => void;
  onAddTagFile: () => void;
  onRemoveTagFile: (tfId: string) => void;
}> = ({
  idx,
  item,
  existingNames,
  cats,
  versionError,
  onNameSelect,
  onChange,
  onRemove,
  onTagFileChange,
  onAddTagFile,
  onRemoveTagFile,
}) => {
  return (
    <div
      style={{
        border: '1px solid #e5e7eb',
        borderRadius: 8,
        padding: 12,
        marginBottom: 12,
        background: '#fafafa',
      }}
    >
      {/* 基础字段:name / version / category / type */}
      <div style={{ display: 'grid', gridTemplateColumns: '1.6fr 1fr 1fr 1fr', gap: 10 }}>
        <div>
          <label style={labelStyle}>
            模型类别 <span style={{ color: '#dc2626' }}>*</span>
          </label>
          <Select
            style={{ width: '100%' }}
            showSearch
            allowClear
            value={item.name || undefined}
            onChange={(v) => {
              const name = (v || '').trim();
              if (name) {
                onNameSelect(name);
              } else {
                onChange({ name: '' });
              }
            }}
            placeholder="选择模型类别"
            options={existingNames.map((n) => ({ label: n, value: n }))}
            notFoundContent="没有允许上传的模型类别（请联系管理员在系统配置中维护）"
          />
        </div>
        <div>
          <label style={labelStyle}>
            版本号 <span style={{ color: '#dc2626' }}>*</span>
          </label>
          <Input
            value={item.version}
            status={versionError ? 'error' : undefined}
            onChange={(e) => onChange({ version: e.target.value })}
            placeholder="v1.2.0"
          />
          {versionError && <div style={{ color: '#dc2626', fontSize: 12, marginTop: 4 }}>{versionError}</div>}
        </div>
        <div>
          <label style={labelStyle}>一级分类</label>
          <Select
            style={{ width: '100%' }}
            value={item.category}
            onChange={(v) => onChange({ category: v })}
            options={CATEGORY_OPTIONS}
            allowClear
            placeholder="perception..."
          />
        </div>
        <div>
          <label style={labelStyle}>二级分类 type</label>
          <Input
            value={item.type}
            onChange={(e) => onChange({ type: e.target.value })}
            placeholder="bev / lane / od..."
          />
          {ndpTypeLabel(cats, item.type) && (
            <div style={{ fontSize: 12, color: '#8a8f99', marginTop: 2 }}>中台模型类别：{ndpTypeLabel(cats, item.type)}</div>
          )}
        </div>
      </div>

      {/* 格式产物:多个 (tag, file) 二元组 */}
      <div
        style={{
          marginTop: 14,
          paddingTop: 12,
          borderTop: '1px dashed #e5e7eb',
        }}
      >
        <div style={{ fontSize: 12, color: '#4b5563', fontWeight: 500, marginBottom: 8 }}>
          格式产物 (tag → file)
        </div>
        {item.tagFiles.map((tf, tfIdx) => (
          <TagFileRow
            key={tf.id}
            tf={tf}
            canRemove={item.tagFiles.length > 1}
            onChange={(patch) => onTagFileChange(tf.id, patch)}
            onRemove={() => onRemoveTagFile(tf.id)}
            isFirst={tfIdx === 0}
          />
        ))}
        <Button
          type="dashed"
          size="small"
          icon={<PlusOutlined />}
          onClick={onAddTagFile}
          style={{ marginTop: 4 }}
        >
          添加其他格式
        </Button>
      </div>
    </div>
  );
};

const TagFileRow: React.FC<{
  tf: TagFile;
  canRemove: boolean;
  isFirst: boolean;
  onChange: (patch: Partial<TagFile>) => void;
  onRemove: () => void;
}> = ({ tf, canRemove, isFirst, onChange, onRemove }) => {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '160px 1fr auto',
        gap: 10,
        alignItems: 'end',
        marginBottom: 8,
      }}
    >
      <div>
        {isFirst && (
          <label style={labelStyle}>
            tag (格式) <span style={{ color: '#dc2626' }}>*</span>
          </label>
        )}
        <Select
          style={{ width: '100%' }}
          mode="tags"
          maxCount={1}
          value={tf.tag ? [tf.tag] : []}
          onChange={(vals) => onChange({ tag: (vals[vals.length - 1] || '').trim() })}
          placeholder="onnx"
          options={TAG_PRESETS.map((t) => ({ label: t, value: t }))}
        />
      </div>
      <div>
        {isFirst && (
          <label style={labelStyle}>
            文件 <span style={{ color: '#dc2626' }}>*</span>
          </label>
        )}
        <FilePicker file={tf.file} onPick={(f) => onChange({ file: f })} />
      </div>
      <div style={{ paddingBottom: 4 }}>
        {canRemove && (
          <Button
            size="small"
            type="text"
            danger
            icon={<CloseOutlined />}
            onClick={onRemove}
            title="移除该格式"
          />
        )}
      </div>
    </div>
  );
};

const FilePicker: React.FC<{ file?: File; onPick: (f: File) => void }> = ({ file, onPick }) => {
  return (
    <label
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '6px 12px',
        border: '1px dashed #d9d9d9',
        borderRadius: 6,
        cursor: 'pointer',
        background: '#fff',
        fontSize: 12,
        color: file ? '#1d1d1f' : '#8c8c8c',
        minHeight: 32,
      }}
    >
      <InboxOutlined />
      {file ? (
        <>
          <span style={{ fontFamily: 'monospace' }}>{file.name}</span>
          <span style={{ color: '#8c8c8c' }}>
            ({(file.size / 1024 ** 2).toFixed(1)} MB)
          </span>
        </>
      ) : (
        <span>点击选择文件</span>
      )}
      <input
        type="file"
        style={{ display: 'none' }}
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) onPick(f);
        }}
      />
    </label>
  );
};

const labelStyle: React.CSSProperties = {
  fontSize: 11,
  color: '#4b5563',
  fontWeight: 500,
  display: 'block',
  marginBottom: 4,
};

export default Upload;
